import { connect } from 'react-redux'
import Draggable from 'react-draggable'
import bowser from 'bowser'

const defaultCodec = {
  safari: { codec: 'video/mp4', fileExt: 'mp4' },
  default: { codec: 'video/webm', fileExt: 'webm' },
}
const defaultBitrate = 20
const bitrateMultiplier = 1000000 // mega, 10^6
const CODECS = [
  'video/webm',
  'video/webm;codecs=h264',
  'video/webm;codecs=avc1',
  'video/webm;codecs=vp8',
  'video/webm;codecs=vp9',
  'video/webm;codecs=h264,vp9',
  'video/webm;codecs=vp8,vp9',

  'video/x-matroska',
  'video/x-matroska;codecs=avc1'
]

@connect(state => ({ showRecordingControls: state.base.showRecordingControls }))
export default class RecordingControls extends React.Component {
  constructor() {
    super()
    this.defaultCodec =  bowser.safari ? defaultCodec.safari : defaultCodec.default
    this.recordedBlobs = []
    this.mediaRecorder = undefined
    this.latestRecordingFPS = 60
    this.currentFPS = 60
    this.currentVideoBitrate = defaultBitrate
    this.currentDuration = new Date(null)
    this.startTime = null
    this.endTime = null
    this.currentFileName = 'gamevideo'
  }

  state = {
    latestRecording: { frames: 0, fps: 0 },
  }
  
  componentDidMount() {
    this.currentVideoCodec = MediaRecorder.isTypeSupported ? CODECS.find(c => MediaRecorder.isTypeSupported(c)) : this.defaultCodec.codec
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.showRecordingControls !== this.props.showRecordingControls) {
      if (!nextProps.showRecordingControls) {
        this.mediaRecorder && this.mediaRecorder.active && this.mediaRecorder.stop()
        this.recordedBlobs = []
        this.setState({ recording: false, latestRecording: { frames: 0, fps: 0 } })
      }
    }
  }

  /**
   * Starts or stops the recording.
   */
  toggleRecord = () => {
    if (this.state.recording && this.mediaRecorder) {
      this.mediaRecorder.stop()
      this.stopTracks()
      return
    }

    if (!this.canvas) {
      this.canvas = document.querySelector('canvas')
    }

    this.stopTracks()
    this.stream = this.canvas.captureStream(this.currentFPS)
    this.startRecording()
    this.startTime = Date.now()

    this.setState({ recording: true })
  }

  /**
   * Stop all tracks in the current stream.
   */
  stopTracks = () => {
    if (this.stream) {
      this.stream.getTracks().forEach(t => {
        t.stop()
        this.stream.removeTrack(t)
      })
    }
  }

  /**
   * Create a MediaRecorder to record the video.
   */
  startRecording = () => {
    const options = {
      mimeType: this.currentVideoCodec,
      videoBitsPerSecond: (this.currentVideoBitrate * bitrateMultiplier)  || (defaultBitrate * bitrateMultiplier),
    }

    this.recordedBlobs = []
    this.mediaRecorder = new MediaRecorder(this.stream, options)
    this.mediaRecorder.ondataavailable = this.handleDataAvailable
    // TODO: Display duration of recorded video.
    this.mediaRecorder.onstop = this.handleStop
    this.mediaRecorder.start(100) // Timeslice of 100ms / blob
  }

  /**
   * Update the FPS variable when the input changes.
   */
  updateFPS = (e) => {
    const newValue = Number(e.target.value)
    if (isNaN(newValue)) {
      console.error('Incorrect FPS value', e.target.value)
      return
    }

    this.currentFPS = newValue
  }

  /**
   * Update the bitrate variable when the input changes.
   */
  updateBitrate = (e) => {
    const newValue = Number(e.target.value)
    if (isNaN(newValue)) {
      console.error('Incorrect FPS value', e.target.value)
      return
    }

    this.currentVideoBitrate = newValue
  }

  /**
   * Store the data when the MediaRecorder dispatches the 'ondataavailable' event.
   */
  handleDataAvailable = (event) => {
    if (event.data && event.data.size > 0) {
      this.recordedBlobs.push(event.data)
    }
  }

  /**
   * Set the state when the MediaRecorder has stopped recording.
   */
  handleStop = () => {
    this.setState({
      recording: false,
      latestRecording: {
        frames: this.recordedBlobs.length,
        fps: this.currentFPS,
        bitrate: this.currentVideoBitrate,
        codec: this.currentVideoCodec
      }
    })
  }

  /**
   * Download the latest recorded video.
   */
  download = () => {
    const blob = new Blob(this.recordedBlobs, { type: this.currentVideoCodec })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = url
    a.download = `${this.currentFileName}.${this.currentVideoCodec.indexOf('matroska') > -1 ? 'mkv' : this.defaultCodec.fileExt}`
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }, 100)
  }

  /**
   * Update the stored codec string when the input changes.
   */
  onCodecChanged = (e) => {
    this.currentVideoCodec = e.target.value
  }

  /**
   * Return all codecs from the array which are supported in the browser.
   */
  filteredCodecs = () => {
    return MediaRecorder.isTypeSupported ? CODECS.filter(c => MediaRecorder.isTypeSupported(c)) : [ this.defaultCodec.codec ]
  }

  /**
   * Store the file name when the input changes.
   */
  setFileName = (e) => {
    this.currentFileName = e.target.value
  }

  render() {
    const { recording, latestRecording } = this.state
    const { showRecordingControls } = this.props
    const availableCodecs = this.filteredCodecs()

    return (
      <Draggable handle={".handle"}>
        <div id="recording" style={{ display: showRecordingControls ? 'block' : 'none' }}>
          <div className="handle">Drag</div>
          <div className="title">Recording Controls</div>
          <div className="recordingControls">
            <div className="record">
              <div>
                <input type="button" value={recording ? 'Stop recording' : 'Start recording'} onClick={this.toggleRecord} />
                <div className="recordingMsg" style={{ display: recording ? 'block' : 'none' }}>{`Recording...`}</div>
                <div className="option" style={{ display: recording ? 'none' : 'block' }}>
                  <p>Video codec:</p>
                  <select name="codecs" onChange={this.onCodecChanged}>
                    { availableCodecs.map(c => (
                      <option key={`option-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="fps option" style={{ display: recording ? 'none' : 'block' }}>
                  <p>Max FPS:</p>
                  <input type="number" disabled={recording} onChange={this.updateFPS} defaultValue={60} />
                </div>
                <div className="bitrate option" style={{ display: recording ? 'none' : 'block' }}>
                  <p>Bitrate (mbps): </p>
                  <input step={0.01} type="number" disabled={recording} onChange={this.updateBitrate} defaultValue={defaultBitrate} />
                </div>
              </div>
              <div className="divider" />
              <div>
                <input id="downloadButton" type="button" value="Download" disabled={recording || !latestRecording.frames} onClick={this.download} />
                { (!recording && latestRecording.frames > 0) && (
                  <div className="option">
                    <p>Filename:</p>
                    <input type="text" onChange={this.setFileName} defaultValue="gamevideo" />
                  </div> )}
                { (!recording && latestRecording.frames > 0) && (
                <div className="recordedInfo">
                  <p>{`Recorded at using:`}</p>
                  <p>{`- Max FPS: ${latestRecording.fps} FPS`}</p>
                  <p>{`- Bit rate (mbps): ${latestRecording.bitrate}`}</p>
                  <p>{`- Video codec: ${latestRecording.codec}`}</p>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Draggable>
    )
  }
}
